// ===== Packages =====

import {
    Descendant,
    Text,
} from 'slate';

interface ISerializeSlatePlainTextArgs {
    slateValue: Descendant[],
    blockSeparator?: string,
    excludeFigureCaptions?: boolean,
    includeSectionHeadingPrefixes?: boolean,
}

const FIGURE_SPEECH_EXCLUDED_NODE_TYPES = new Set([
    'caption',
    'figure-content',
]);

function isSlateElement(value: Descendant): value is Descendant & {
    type?: string,
    headerSize?: number,
    children?: Descendant[],
} {
    return !!value
        && typeof value === 'object'
        && !Text.isText(value);
}

function isHeaderTwoElement(node: Descendant): boolean {
    return isSlateElement(node)
        && node.type === 'header'
        && node.headerSize === 2;
}

function shouldExcludeNodeFromSpeechText({
    node,
    excludeFigureCaptions,
}: {
    node: Descendant,
    excludeFigureCaptions: boolean,
}): boolean {
    return excludeFigureCaptions
        && isSlateElement(node)
        && typeof node.type === 'string'
        && FIGURE_SPEECH_EXCLUDED_NODE_TYPES.has(node.type);
}

/**
 * Serializes the current Slate scope into readable plain text so audio-note
 * dictation alignment can reuse the exact copy visible in the editor.
 */
const serializeSlatePlainText = ({
    slateValue,
    blockSeparator = '\n\n',
    excludeFigureCaptions = false,
    includeSectionHeadingPrefixes = false,
}: ISerializeSlatePlainTextArgs): string => {
    let sectionHeadingCount = 0;

    /**
     * Walks Slate content in document order while allowing speech generation
     * to skip non-reading text such as figure captions.
     */
    const serializeNode = (node: Descendant): string => {
        if (shouldExcludeNodeFromSpeechText({
            node,
            excludeFigureCaptions,
        })) {
            return '';
        }

        if (Text.isText(node)) {
            return node.text || '';
        }

        if (!isSlateElement(node) || !Array.isArray(node.children)) {
            return '';
        }

        const serializedChildren = node.children
            .map((child) => serializeNode(child))
            .filter((text) => text.trim().length > 0)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();

        if (
            includeSectionHeadingPrefixes
            && serializedChildren
            && isHeaderTwoElement(node)
        ) {
            sectionHeadingCount += 1;
            return `Section ${sectionHeadingCount}, ${serializedChildren}`;
        }

        return serializedChildren;
    };

    return slateValue
        .map((node) => serializeNode(node).trim())
        .filter((text) => text.length > 0)
        .join(blockSeparator)
        .trim();
};

export default serializeSlatePlainText;
